/**
 * Type-aware filter operators for the smart-list builder.
 *
 * Each "field kind" has a different set of operators (you can't "is greater
 * than" a string, you can't "contains" a number). This file owns the mapping
 * between field types and their available operators + how each operator's
 * value input is rendered.
 *
 * Mirrors the operator vocabulary in /backend/src/lib/list-filter.ts.
 */

export type FieldKind =
  | "text" | "number" | "boolean" | "date" | "datetime"
  | "dropdown" | "multiselect"
  | "tag"            // special — operates on the tags table
  | "lifecycle"      // dropdown of lifecycle stages
  | "owner";         // dropdown of users

export interface FieldOption {
  /** Internal field identifier — passed to the backend filter compiler.
   *  Examples: "first_name", "email", "tag", "custom.annual_revenue" */
  field: string;
  label: string;
  kind: FieldKind;
  /** For dropdown/multiselect/lifecycle/owner: the choices */
  options?: string[];
  group?: string;
}

export interface OperatorDef {
  op: string;
  label: string;
  /** "none" → no value input; "text" → single text input; "tag" → tag picker;
   *  "options" → dropdown of field's options; "number" → numeric; "date" → date input */
  valueKind: "none" | "text" | "tag" | "options" | "number" | "date" | "boolean";
}

export const OPERATORS_BY_KIND: Record<FieldKind, OperatorDef[]> = {
  text: [
    { op: "contains",     label: "contains",       valueKind: "text" },
    { op: "not_contains", label: "doesn't contain", valueKind: "text" },
    { op: "eq",           label: "is exactly",     valueKind: "text" },
    { op: "neq",          label: "is not",         valueKind: "text" },
    { op: "starts_with",  label: "starts with",    valueKind: "text" },
    { op: "ends_with",    label: "ends with",      valueKind: "text" },
    { op: "is_null",      label: "is empty",       valueKind: "none" },
    { op: "is_not_null",  label: "is not empty",   valueKind: "none" },
  ],
  number: [
    { op: "eq",           label: "equals",         valueKind: "number" },
    { op: "neq",          label: "not equal to",   valueKind: "number" },
    { op: "gt",           label: "is greater than", valueKind: "number" },
    { op: "gte",          label: "is at least",    valueKind: "number" },
    { op: "lt",           label: "is less than",   valueKind: "number" },
    { op: "lte",          label: "is at most",     valueKind: "number" },
    { op: "is_null",      label: "is empty",       valueKind: "none" },
    { op: "is_not_null",  label: "is not empty",   valueKind: "none" },
  ],
  boolean: [
    { op: "eq",           label: "is true",        valueKind: "boolean" },
    { op: "neq",          label: "is false",       valueKind: "boolean" },
    { op: "is_null",      label: "is not set",     valueKind: "none" },
  ],
  date: [
    { op: "newer_than_days", label: "is in the last N days", valueKind: "number" },
    { op: "older_than_days", label: "is more than N days ago", valueKind: "number" },
    { op: "gt",           label: "is after",       valueKind: "date" },
    { op: "lt",           label: "is before",      valueKind: "date" },
    { op: "eq",           label: "is on",          valueKind: "date" },
    { op: "is_null",      label: "is not set",     valueKind: "none" },
    { op: "is_not_null",  label: "is set",         valueKind: "none" },
  ],
  datetime: [
    { op: "newer_than_days", label: "is in the last N days", valueKind: "number" },
    { op: "older_than_days", label: "is more than N days ago", valueKind: "number" },
    { op: "gt",           label: "is after",       valueKind: "date" },
    { op: "lt",           label: "is before",      valueKind: "date" },
    { op: "is_null",      label: "is not set",     valueKind: "none" },
    { op: "is_not_null",  label: "is set",         valueKind: "none" },
  ],
  dropdown: [
    { op: "eq",           label: "is",             valueKind: "options" },
    { op: "neq",          label: "is not",         valueKind: "options" },
    { op: "is_null",      label: "is not set",     valueKind: "none" },
  ],
  multiselect: [
    { op: "contains",     label: "includes",       valueKind: "options" },
    { op: "not_contains", label: "doesn't include", valueKind: "options" },
  ],
  tag: [
    { op: "has_tag",      label: "has tag",        valueKind: "tag" },
    { op: "no_tag",       label: "doesn't have tag", valueKind: "tag" },
  ],
  lifecycle: [
    { op: "eq",           label: "is",             valueKind: "options" },
    { op: "neq",          label: "is not",         valueKind: "options" },
  ],
  owner: [
    { op: "eq",           label: "is",             valueKind: "options" },
    { op: "neq",          label: "is not",         valueKind: "options" },
    { op: "is_null",      label: "is unassigned",  valueKind: "none" },
  ],
};

/** Static set of contact fields that always exist. Custom fields are added at runtime. */
export const STANDARD_FIELDS: FieldOption[] = [
  { field: "name",            label: "Name",            kind: "text",      group: "Identity" },
  { field: "first_name",      label: "First name",      kind: "text",      group: "Identity" },
  { field: "last_name",       label: "Last name",       kind: "text",      group: "Identity" },
  { field: "email",           label: "Email",           kind: "text",      group: "Identity" },
  { field: "phone",           label: "Phone",           kind: "text",      group: "Identity" },
  { field: "tag",             label: "Tag",             kind: "tag",       group: "Tags" },
  { field: "lifecycle_stage", label: "Lifecycle stage", kind: "lifecycle", options: ["lead","contact","customer","churned"], group: "Status" },
  { field: "source",          label: "Source",          kind: "text",      group: "Status" },
  { field: "owner_id",        label: "Owner",           kind: "owner",     group: "Status" },
  { field: "city",            label: "City",            kind: "text",      group: "Location" },
  { field: "state",           label: "State / Region",  kind: "text",      group: "Location" },
  { field: "country",         label: "Country",         kind: "text",      group: "Location" },
  { field: "do_not_contact",  label: "Do not contact",  kind: "boolean",   group: "Status" },
  { field: "created_at",      label: "Date added",      kind: "datetime",  group: "Timing" },
  { field: "last_activity_at",label: "Last activity",   kind: "datetime",  group: "Timing" },

  // The criteria she builds audiences from in Boulevard [22:25] — provider
  // seen, service received, upcoming appointment, appointment count, first
  // and last appointment date, product purchased. This is what turns
  // "everyone on the weight-loss programme" into a list the nurses can work.
  { field: "last_visit",        label: "Last visit",              kind: "datetime", group: "Visits" },
  { field: "first_visit",       label: "First visit",             kind: "datetime", group: "Visits" },
  { field: "visit_count",       label: "Number of visits",        kind: "number",   group: "Visits" },
  { field: "no_show_count",     label: "Number of no-shows",      kind: "number",   group: "Visits" },
  { field: "has_upcoming",      label: "Has an upcoming visit",   kind: "boolean",  group: "Visits" },
  { field: "service_received",  label: "Treatment received",      kind: "text",     group: "Visits" },
  { field: "provider_seen",     label: "Provider seen",           kind: "text",     group: "Visits" },
  { field: "product_purchased", label: "Product bought",          kind: "text",     group: "Money" },
  { field: "membership_active", label: "Has a membership",        kind: "boolean",  group: "Money" },
  { field: "package_outstanding", label: "Has visits left on a package", kind: "boolean", group: "Money" },
];
